import moment from 'moment-timezone';
import { SingleProfitCalculationApiResponse } from '../../../../ProfitCalculations/types';
import { tokens } from '../tokens';
import {
  TimeAxisInterval,
  TimeAxisUnit,
  buildTimeAxis,
} from '../../../../../helpers/Chart';
import { fmtPct, isCore, txProfitValue } from './format';

export interface TimePoint {
  x: number;
  y: number;
}

export interface HeatmapCell {
  pct: number;
  count: number;
}

export interface HistogramBin {
  binStart: number;
  binEnd: number;
  count: number;
}

export interface StreakStats {
  longestWin: number;
  longestLoss: number;
  avgStreak: number;
}

export const median = (values: number[]): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

export const coreProfits = (
  spc: SingleProfitCalculationApiResponse,
): number[] =>
  spc.transactions
    .filter((tx) => isCore(tx.transactionType))
    .sort(
      (a, b) =>
        (a.transactionTimestamp ?? a.timestamp) -
        (b.transactionTimestamp ?? b.timestamp),
    )
    .map(txProfitValue);

/** Profit of every transaction (core + take-profit + stop-loss), chronological. */
export const allProfits = (spc: SingleProfitCalculationApiResponse): number[] =>
  [...spc.transactions]
    .sort(
      (a, b) =>
        (a.transactionTimestamp ?? a.timestamp) -
        (b.transactionTimestamp ?? b.timestamp),
    )
    .map(txProfitValue);

export const computeStreaks = (profits: number[]): StreakStats => {
  if (!profits.length) {
    return { longestWin: 0, longestLoss: 0, avgStreak: 0 };
  }
  let longestWin = 0;
  let longestLoss = 0;
  let currentWin = 0;
  let currentLoss = 0;
  const runLengths: number[] = [];
  let runIsWin: boolean | null = null;
  let runLength = 0;

  for (const profit of profits) {
    const isWin = profit > 0;
    if (runIsWin === null || runIsWin === isWin) {
      runLength++;
    } else {
      runLengths.push(runLength);
      runLength = 1;
    }
    runIsWin = isWin;
    if (isWin) {
      currentWin++;
      currentLoss = 0;
      longestWin = Math.max(longestWin, currentWin);
    } else {
      currentLoss++;
      currentWin = 0;
      longestLoss = Math.max(longestLoss, currentLoss);
    }
  }
  if (runLength) runLengths.push(runLength);
  const avgStreak = runLengths.length
    ? runLengths.reduce((a, b) => a + b, 0) / runLengths.length
    : 0;
  return { longestWin, longestLoss, avgStreak };
};

export const streakSeries = (
  profits: number[],
): Array<{ win: boolean; profit: number }> =>
  profits.map((profit) => ({ win: profit > 0, profit }));

export const histogram = (profits: number[], binCount = 12): HistogramBin[] => {
  if (!profits.length) return [];
  const min = Math.min(...profits);
  const max = Math.max(...profits);
  if (min === max) {
    return [{ binStart: min, binEnd: max, count: profits.length }];
  }
  const width = (max - min) / binCount;
  // Anchor the bin grid on 0 so no bin straddles break-even — a straddling
  // bin mixes losses and gains and would be coloured by its midpoint alone,
  // hiding the losing trades.
  const firstEdge = Math.floor(min / width) * width;
  const bins: HistogramBin[] = [];
  for (let edge = firstEdge; edge < max; edge += width) {
    bins.push({ binStart: edge, binEnd: edge + width, count: 0 });
  }
  for (const profit of profits) {
    const binIndex = Math.min(
      bins.length - 1,
      Math.max(0, Math.floor((profit - firstEdge) / width)),
    );
    bins[binIndex].count++;
  }
  return bins;
};

const downsample = (
  entries: Array<[number, number]>,
  max = 600,
): TimePoint[] => {
  if (entries.length <= max) {
    return entries.map(([x, y]) => ({ x, y }));
  }
  const step = Math.ceil(entries.length / max);
  return entries.filter((_, i) => i % step === 0).map(([x, y]) => ({ x, y }));
};

const historyEntries = (
  map: Record<string, number> | undefined,
): Array<[number, number]> => {
  if (!map) return [];
  return Object.entries(map)
    .map(([key, value]) => [parseInt(key, 10), value] as [number, number])
    .sort((a, b) => a[0] - b[0]);
};

export const equityCurve = (
  spc: SingleProfitCalculationApiResponse,
): { stable: TimePoint[]; asset: TimePoint[]; price: TimePoint[] } => {
  const stableEntries = historyEntries(spc.endBalanceInUsdtHistory);
  const assetEntries = historyEntries(spc.endBalanceInAssetHistory);
  const priceEntries = historyEntries(spc.priceHistory);

  const startPrice = priceEntries[0]?.[1] ?? 1;
  const startBalance = stableEntries[0]?.[1] ?? spc.startBalanceInUsdt ?? 0;

  return {
    stable: downsample(stableEntries),
    asset: downsample(
      assetEntries.map(
        ([timestamp, asset]) =>
          [
            timestamp,
            asset *
              (priceEntries.find(
                ([priceTimestamp]) => priceTimestamp >= timestamp,
              )?.[1] ?? startPrice),
          ] as [number, number],
      ),
    ),
    price: downsample(
      priceEntries.map(
        ([timestamp, price]) =>
          [timestamp, (price / startPrice) * (startBalance || 1)] as [
            number,
            number,
          ],
      ),
    ),
  };
};

const groupHistoryByMonth = (
  entries: Array<[number, number]>,
): Map<string, { first: number; last: number }> => {
  const grouped = new Map<string, { first: number; last: number }>();
  for (const [timestamp, value] of entries) {
    const key = moment(timestamp).utc().format('YYYY-MM');
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, { first: value, last: value });
    } else {
      existing.last = value;
    }
  }
  return grouped;
};

export const computeHeatmap = (
  spc: SingleProfitCalculationApiResponse,
): Record<number, Record<number, HeatmapCell>> => {
  const entries = historyEntries(spc.endBalanceInUsdtHistory);
  const grouped = groupHistoryByMonth(entries);
  const result: Record<number, Record<number, HeatmapCell>> = {};
  let prevLast: number | null = null;
  for (const [key, { first, last }] of grouped) {
    const [yearStr, monthStr] = key.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    const base = prevLast ?? first;
    const pct = base > 0 ? (last - base) / base : 0;
    if (!result[year]) result[year] = {};
    result[year][month] = { pct, count: 1 };
    prevLast = last;
  }
  return result;
};

export interface FooterCell {
  label: string;
  growth: string;
  color: string;
  /** Visible duration (ms) — drives the proportional column width. */
  weight: number;
}

export interface FooterModel {
  unit: TimeAxisUnit;
  cells: FooterCell[];
}

/**
 * Per-interval growth of a balance history — the change across each
 * interval's visible span, sampled at the interval boundaries.
 */
const intervalGrowth = (
  history: Record<string, number> | undefined,
  intervals: TimeAxisInterval[],
): Array<{ growth: string; color: string }> => {
  const entries = historyEntries(history);
  if (!entries.length) {
    return intervals.map(() => ({ growth: '—', color: tokens.textMuted }));
  }
  // entries are sorted ascending — binary-search the nearest sample.
  const valueAt = (timestamp: number): number => {
    let lo = 0;
    let hi = entries.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (entries[mid][0] < timestamp) lo = mid + 1;
      else hi = mid;
    }
    if (
      lo > 0 &&
      Math.abs(entries[lo - 1][0] - timestamp) <=
        Math.abs(entries[lo][0] - timestamp)
    ) {
      return entries[lo - 1][1];
    }
    return entries[lo][1];
  };
  return intervals.map((interval) => {
    const startValue = valueAt(interval.startTs);
    const endValue = valueAt(interval.endTs);
    const pct = startValue > 0 ? (endValue - startValue) / startValue : 0;
    return {
      growth: fmtPct(pct, true).replace('-', '−'),
      color: pct < 0 ? tokens.red : tokens.green,
    };
  });
};

/**
 * The adaptive x-axis footer for the profit chart: one cell per calendar
 * interval (year/quarter/month/week — picked from the span), each carrying
 * its label and the balance growth over that interval.
 */
export const buildFooterModel = (
  history: Record<string, number> | undefined,
  domainStart: number,
  domainEnd: number,
): FooterModel => {
  const { unit, intervals } = buildTimeAxis(domainStart, domainEnd);
  const growth = intervalGrowth(history, intervals);
  return {
    unit,
    cells: intervals.map((interval, index) => ({
      label: interval.label,
      growth: growth[index].growth,
      color: growth[index].color,
      weight: Math.max(1, interval.endTs - interval.startTs),
    })),
  };
};

export const monthlyAverageProfit = (
  spc: SingleProfitCalculationApiResponse,
): { avg: number; totalMonths: number; profitableMonths: number } => {
  const entries = historyEntries(spc.endBalanceInUsdtHistory);
  if (!entries.length) return { avg: 0, totalMonths: 0, profitableMonths: 0 };
  const grouped = groupHistoryByMonth(entries);
  const monthlyChanges: number[] = [];
  let prevLast: number | null = null;
  for (const { first, last } of grouped.values()) {
    const base = prevLast ?? first;
    if (base > 0) monthlyChanges.push((last - base) / base);
    prevLast = last;
  }
  if (!monthlyChanges.length) {
    return { avg: 0, totalMonths: 0, profitableMonths: 0 };
  }
  return {
    avg: monthlyChanges.reduce((a, b) => a + b, 0) / monthlyChanges.length,
    totalMonths: monthlyChanges.length,
    profitableMonths: monthlyChanges.filter((change) => change > 0).length,
  };
};
